/** Klien HTTP terpusat untuk API ISTN Activity Hub. */

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Endpoint publik: tidak boleh memicu percobaan refresh sesi. */
const NO_REFRESH_PATHS = new Set(["/auth/login", "/auth/refresh", "/auth/csrf"]);

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  /** Penanda internal agar percobaan ulang hanya terjadi sekali. */
  retry?: boolean;
}

interface ErrorPayload {
  error?: { code?: string; message?: string };
}

function errorCodeOf(payload: unknown): string | null {
  const code = (payload as ErrorPayload | null)?.error?.code;
  return typeof code === "string" ? code : null;
}

function errorMessageOf(payload: unknown): string {
  const message = (payload as ErrorPayload | null)?.error?.message;
  return typeof message === "string" ? message : "Terjadi kesalahan. Silakan coba lagi.";
}

async function parsePayload(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return text;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = { Accept: "application/json", ...options.headers };
  let body: BodyInit | undefined;

  if (options.body instanceof FormData) {
    // Boundary multipart ditetapkan otomatis oleh browser.
    body = options.body;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  if (MUTATING_METHODS.has(method) && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  return fetch(`${API_BASE}${path}`, { method, headers, body, credentials: "include" });
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Perbarui sesi memakai refresh token (cookie httpOnly).
 * Dipanggil otomatis sekali saat permintaan dijawab 401.
 */
export async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) return false;
        const data = (await res.json()) as { csrfToken?: string };
        if (typeof data.csrfToken === "string") setCsrfToken(data.csrfToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

/** Ambil token CSRF baru (jalur publik, aman dipanggil sebelum login). */
export async function loadCsrfToken(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/csrf`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { csrfToken?: string };
    if (typeof data.csrfToken === "string") {
      setCsrfToken(data.csrfToken);
      return data.csrfToken;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Permintaan API dengan cookie sesi.
 * - 401 → coba refresh sesi sekali, lalu ulangi permintaan.
 * - 403 CSRF_INVALID → ambil token CSRF baru, lalu ulangi sekali.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await send(path, options);

  if (res.status === 401 && !options.retry && !NO_REFRESH_PATHS.has(path)) {
    if (await refreshSession()) res = await send(path, { ...options, retry: true });
  }

  if (res.status === 403 && !options.retry) {
    const probe = await parsePayload(res.clone());
    if (errorCodeOf(probe) === "CSRF_INVALID" && (await loadCsrfToken())) {
      res = await send(path, { ...options, retry: true });
    }
  }

  const payload = await parsePayload(res);
  if (!res.ok) {
    throw new ApiError(res.status, errorCodeOf(payload) ?? "UNKNOWN", errorMessageOf(payload));
  }
  return payload as T;
}

/** Unduh berkas biner (mis. bukti kegiatan) sebagai Blob. */
export async function download(path: string): Promise<Blob> {
  let res = await send(path, {});
  if (res.status === 401) {
    if (await refreshSession()) res = await send(path, { retry: true });
  }
  if (!res.ok) {
    const payload = await parsePayload(res);
    throw new ApiError(res.status, errorCodeOf(payload) ?? "UNKNOWN", errorMessageOf(payload));
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
};

/** Bangun query string dari objek; nilai kosong diabaikan. */
export function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}
